import Search16 from '@carbon/icons-react/es/search/16';
import { Moment } from 'moment';
import React, { FC, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Project } from 'api/project';
import { Task } from 'api/tasks';
import { EnabledSourcesConfiguration } from 'api/timesheets/configuration/configuration';
import { ReactComponent as SearchSvg } from 'assets/svgs/search.svg';
import { ReactComponent as TaskListSvg } from 'assets/svgs/task-list.svg';
import { Button, ButtonGroup } from 'components/atoms/button';
import { Input } from 'components/atoms/input';
import { Tag } from 'components/atoms/tag';
import { StatusIndicatorIcon } from 'components/molecules/status-indicator';
import { StatusIndicatorWrapper } from 'components/organisms/timesheets/common/cells/name-cell';
import { DrawerContent } from 'components/organisms/timesheets/common/drawer';
import { useTranslate } from 'hooks/use-translation';
import { TimesheetTask } from '../../types';
import { Content, ProjectName, SearchInput, TaskName } from '../components';
import { TasksList } from '../task-list';
import { ProjectSelect } from './project-select';
import { TaskListItem, useAddTasksActions } from './use-add-tasks-actions';

const FilterContainer = styled.div`
  padding: var(--spacing-06);
  border-bottom: 1px solid var(--border-color);
`;

const FilterOptions = styled.div`
  ${Tag} {
    margin-right: var(--spacing-03);
  }
`;

const TaskWrapper = styled.div`
  width: 300px;
  display: flex;
  gap: var(--spacing-03);
`;

export const AddTasksDrawerContent: FC<{
  projects: Project[];
  startDate: Moment;
  alreadyAddedTasks: TimesheetTask[];
  onAddTasks: (taskList: Task[]) => void;
  timesheetEntriesConfig: EnabledSourcesConfiguration;
}> = ({
  projects,
  alreadyAddedTasks,
  onAddTasks,
  startDate,
  timesheetEntriesConfig,
}) => {
  const translate = useTranslate();
  const {
    projectOptions,
    projectSelected,
    setProjectSelected,
    filterOptions,
    selectedFilter,
    onFilterSelect,
    isTasksLoading,
    taskList,
    selectedTasks,
    onTaskSelected,
    selectedAllDisplayingTasks,
    searchText,
    searchTasks,
    clearSelectedTasks,
    clearSearchText,
  } = useAddTasksActions({
    projects,
    alreadyAddedTasks,
    startDate,
    timesheetEntriesConfig,
  });

  const searchInputRef = useRef<any>(null);

  const clearSearch = () => {
    clearSearchText();
    if (searchInputRef?.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <Content>
      <DrawerContent.Title data-cy="timesheets.task-drawer.add_tasks">
        <FormattedMessage id="timesheets.title.add_tasks" />
      </DrawerContent.Title>
      <DrawerContent.Content>
        <FilterContainer data-cy="timesheets.task-drawer.filter">
          <ProjectSelect
            options={projectOptions}
            value={projectSelected}
            onChange={setProjectSelected}
          />
          <FilterOptions data-cy="timesheets.task-drawer.individual-filters">
            {filterOptions.map((filterOption) => (
              <Tag
                onClick={() => onFilterSelect(filterOption)}
                color={filterOption === selectedFilter ? 'blue' : 'cool-gray'}
              >
                <FormattedMessage
                  id={`timesheets.tasks.filters.${filterOption}`}
                />
              </Tag>
            ))}
          </FilterOptions>
        </FilterContainer>
        <SearchInput>
          <Input
            data-cy="timesheets.task-drawer.search"
            ref={searchInputRef}
            value={searchText}
            placeholder={translate('timesheets.placeholders.search_task')}
            onChange={searchTasks}
          />
          <Search16 />
        </SearchInput>
        <TasksList
          title={
            <FormattedMessage
              id={`timesheets.tasks.filters_header.${selectedFilter}`}
            />
          }
          loading={isTasksLoading}
          list={taskList}
          disabledTooltipContent={
            <FormattedMessage id="timesheets.tasks.already_added.disabled" />
          }
          selectAll={selectedAllDisplayingTasks}
          onSelected={onTaskSelected}
          renderItem={(task: TaskListItem) => {
            const getTaskField = (fields, fieldName) =>
              fields.find((field) => field.fieldName === fieldName);

            return (
              <TaskWrapper>
                <StatusIndicatorWrapper>
                  <StatusIndicatorIcon
                    group={getTaskField(task.fields, 'Status')?.fieldValue}
                    type={task.type}
                    progress={task.progress}
                  />
                </StatusIndicatorWrapper>
                <div>
                  <TaskName>{task.taskName}</TaskName>
                  <ProjectName>{task.project.projectName}</ProjectName>
                </div>
              </TaskWrapper>
            );
          }}
          empty={
            searchText.length === 0 ? (
              <>
                <TaskListSvg />
                <div>
                  <FormattedMessage id="timesheets.tasks.filters.error.no_tasks" />
                </div>
              </>
            ) : (
              <>
                <SearchSvg />
                <div>
                  <FormattedMessage id="timesheets.tasks.filters.error.no_search_result_line_1" />
                </div>
                <div>
                  <FormattedMessage id="timesheets.tasks.filters.error.no_search_result_line_2" />
                </div>
                <Button type="link" onClick={clearSearch}>
                  <FormattedMessage id="timesheets.tasks.filters.actions.clear_search" />
                </Button>
              </>
            )
          }
        />
      </DrawerContent.Content>
      <DrawerContent.Footer>
        <ButtonGroup $reversed>
          <Button
            data-cy="timesheets.task-drawer.add"
            iconPlacement="right"
            type="primary"
            onClick={() => {
              onAddTasks(selectedTasks);
              clearSelectedTasks();
            }}
            disabled={selectedTasks.length === 0}
          >
            <FormattedMessage
              id="timesheets.buttons.add_selected_task"
              values={{ count: selectedTasks.length }}
            />
          </Button>
          <Button
            data-cy="timesheets.task-drawer.clear"
            onClick={clearSelectedTasks}
          >
            <FormattedMessage id="clear" />
          </Button>
        </ButtonGroup>
      </DrawerContent.Footer>
    </Content>
  );
};
